// Shared wording/formatting for the admin panel, so every screen names the
// same thing the same way (statuses, dates, roles) instead of each view
// carrying its own copy.
import utils from '@/../../shared/utils'

const DATE_FMT = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
const DATETIME_FMT = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function format_date(value){
    return utils.is_valid_parameter(value) ? DATE_FMT.format(new Date(value)) : null;
}

export function format_datetime(value){
    return utils.is_valid_parameter(value) ? DATETIME_FMT.format(new Date(value)) : null;
}

// "3 sept 2025 → 20 oct 2025", tolerating either bound missing.
export function format_range(start, end, fmt = format_date){
    const a = fmt(start), b = fmt(end);
    if(a && b) return a + ' → ' + b;
    if(a) return 'Desde ' + a;
    if(b) return 'Hasta ' + b;
    return 'Sin fechas';
}

// The way back: send an absolute ISO instant. The bare "YYYY-MM-DDTHH:mm"
// the input produces has no zone, and the server read it as UTC - every save
// shifted the dates by the browser's UTC offset (+2h in summer), cumulatively.
export function from_datetime_local(value){
    return utils.is_valid_parameter(value) ? new Date(value).toISOString() : null;
}

// Copy of a draft with its datetime-local fields converted for the server.
export function with_iso_dates(draft, keys = ['pre_start_date', 'start_date', 'end_date']){
    const out = Object.assign({}, draft);
    keys.forEach((k) => { if(k in out) out[k] = from_datetime_local(out[k]); });
    return out;
}

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time.
export function to_datetime_local(value){
    if(!utils.is_valid_parameter(value)) return null;
    const date = new Date(value);
    const pad = (n) => String(n).padStart(2, '0');
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
}

// Pill = { label, cls } where cls is one of the admin.scss .a-pill-* variants.
export function season_status(season, now = Date.now()){
    const t = (v) => utils.is_valid_parameter(v) ? new Date(v).getTime() : null;
    const pre = t(season.pre_start_date), start = t(season.start_date), end = t(season.end_date);
    if(end != null && now > end) return { label: 'Finalizado', cls: 'a-pill-muted' };
    if(start != null && now >= start) return { label: 'En curso', cls: 'a-pill-active' };
    if(pre != null && now >= pre) return { label: 'Inscripción abierta', cls: 'a-pill-ok' };
    if(start != null || pre != null) return { label: 'Próximamente', cls: '' };
    return { label: 'Sin fechas', cls: 'a-pill-muted' };
}

export function stage_status(stage, playback){
    if(playback && playback.stage_id == stage.id) return { label: 'En directo', cls: 'a-pill-live' };
    if(stage.ended_at) return { label: 'Finalizada', cls: 'a-pill-muted' };
    if(stage.started_at) return { label: 'Abierta', cls: 'a-pill-active' };
    return { label: 'Pendiente', cls: '' };
}

export function role_label(role){
    switch(role){
        case utils.USER_ROLES.SUPER_ADMINISTRATOR: return 'Superadministrador';
        case utils.USER_ROLES.ADMINISTRATOR: return 'Administrador';
        case utils.USER_ROLES.ASSISTANT: return 'Asistente';
        case utils.USER_ROLES.MANAGER: return 'Pantalla (manager)';
        case utils.USER_ROLES.PLAYER: return 'Equipo';
        case utils.USER_ROLES.GUEST: return 'Invitado';
    }
    return '—';
}

// The live view a connected client is on, in plain Spanish.
export function view_label(view){
    const map = {
        Home: 'Inicio', Auth: 'Eligiendo equipo', Lobby: 'Sala de espera', Title: 'Título',
        Activity: 'Respondiendo', Result: 'Resultado', Ranking: 'Clasificación', End: 'Final',
        Pause: 'Parada', Interlude: 'Animación', Login: 'Login',
        Panel: 'Panel', Stages: 'Panel', StageRundown: 'Panel', Users: 'Panel', Settings: 'Panel',
        RankingsAdmin: 'Panel', EventParticipants: 'Panel', EventSettings: 'Panel',
    };
    return map[view] || view || '—';
}

export function activity_type_label(type){
    switch(type){
        case utils.ACTIVITY_TYPE.QUIZ: return 'Quiz';
        case utils.ACTIVITY_TYPE.QUIZ_MULTIPLE: return 'Quiz múltiple';
        case utils.ACTIVITY_TYPE.TRUE_FALSE: return 'Verdadero/Falso';
        case utils.ACTIVITY_TYPE.RANGE: return 'Rango';
        case utils.ACTIVITY_TYPE.TEXT: return 'Texto';
        case utils.ACTIVITY_TYPE.CROSSWORD: return 'Crucigrama';
        case utils.ACTIVITY_TYPE.IMAGE: return 'Imagen';
        case utils.ACTIVITY_TYPE.VIDEO: return 'Vídeo';
        case utils.ACTIVITY_TYPE.PDF: return 'PDF';
        case utils.ACTIVITY_TYPE.HTML: return 'HTML';
    }
    return 'Actividad';
}

export function activity_type_icon(type){
    switch(type){
        case utils.ACTIVITY_TYPE.QUIZ: return 'bi-ui-radios';
        case utils.ACTIVITY_TYPE.QUIZ_MULTIPLE: return 'bi-ui-checks';
        case utils.ACTIVITY_TYPE.TRUE_FALSE: return 'bi-toggle-on';
        case utils.ACTIVITY_TYPE.RANGE: return 'bi-sliders2';
        case utils.ACTIVITY_TYPE.TEXT: return 'bi-input-cursor-text';
        case utils.ACTIVITY_TYPE.CROSSWORD: return 'bi-grid-3x3';
        case utils.ACTIVITY_TYPE.IMAGE: return 'bi-image';
        case utils.ACTIVITY_TYPE.VIDEO: return 'bi-play-btn';
        case utils.ACTIVITY_TYPE.PDF: return 'bi-file-earmark-pdf';
        case utils.ACTIVITY_TYPE.HTML: return 'bi-file-richtext';
    }
    return 'bi-question-square';
}

// "1 etapa" / "5 etapas" - plural is singular + "s" unless given.
export function plural(n, singular, plural_form){
    return n + ' ' + (n == 1 ? singular : (plural_form || singular + 's'));
}
